/**
 * Passkey / WebAuthn Helfer (Feb 2026).
 *
 * Kapselt @simplewebauthn/browser + die Backend-Endpunkte:
 *   - loginWithPasskey(email)   -> { user, token }
 *   - registerPasskey(nickname) -> { credential }   (Nutzer muss eingeloggt sein)
 *   - listPasskeys() / deletePasskey(id)
 *
 * „Computer-Login": Windows Hello, Touch ID, Fingerabdruck oder Geräte-PIN.
 */
import { startRegistration, startAuthentication, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill } from "@simplewebauthn/browser";
import api, { formatApiErrorDetail } from "./api";

export const passkeySupported = () => {
    try {
        return browserSupportsWebAuthn();
    } catch {
        return false;
    }
};

/** Conditional UI / Autofill: Browser schlägt Passkey direkt im E-Mail-Feld vor. */
export async function startPasskeyAutofill() {
    try {
        if (!passkeySupported()) return { ok: false, skipped: true };
        if (!(await browserSupportsWebAuthnAutofill())) return { ok: false, skipped: true };
        const { data } = await api.post("/auth/webauthn/login/options", {});
        const asseResp = await startAuthentication({ optionsJSON: data.options, useBrowserAutofill: true });
        const verify = await api.post("/auth/webauthn/login/verify", { flow_id: data.flow_id, credential: asseResp });
        if (verify.data.token) sessionStorage.setItem("tw_token", verify.data.token);
        return { ok: true, user: verify.data.user, token: verify.data.token };
    } catch (e) {
        if (e.name === "NotAllowedError" || e.name === "AbortError") return { ok: false, aborted: true };
        return { ok: false, error: e.response?.data?.detail || e.message };
    }
}

/** Passwortlose Anmeldung mit Passkey. Ohne E-Mail = usernameless (Browser wählt Passkey). */
export async function loginWithPasskey(email) {
    if (!passkeySupported()) return { ok: false, error: "Dieses Gerät/Browser unterstützt keine Passkeys." };
    try {
        const { data } = await api.post("/auth/webauthn/login/options", email ? { email } : {});
        const asseResp = await startAuthentication({ optionsJSON: data.options });
        const verify = await api.post("/auth/webauthn/login/verify", { flow_id: data.flow_id, credential: asseResp });
        if (verify.data.token) sessionStorage.setItem("tw_token", verify.data.token);
        return { ok: true, user: verify.data.user, token: verify.data.token };
    } catch (e) {
        if (e.name === "NotAllowedError" || e.name === "AbortError") {
            return { ok: false, error: "Anmeldung abgebrochen." };
        }
        const detail = e.response?.data?.detail;
        return { ok: false, error: detail ? formatApiErrorDetail(detail) : (e.message || "Passkey-Anmeldung fehlgeschlagen.") };
    }
}

/** Neuen Passkey für den aktuell eingeloggten Nutzer einrichten. */
export async function registerPasskey(nickname) {
    if (!passkeySupported()) return { ok: false, error: "Dieses Gerät/Browser unterstützt keine Passkeys." };
    try {
        const { data: optionsJSON } = await api.post("/auth/webauthn/register/options", {});
        const attResp = await startRegistration({ optionsJSON });
        const { data } = await api.post("/auth/webauthn/register/verify", { credential: attResp, nickname });
        return { ok: true, credential: data.credential };
    } catch (e) {
        if (e.name === "NotAllowedError" || e.name === "AbortError") {
            return { ok: false, error: "Einrichtung abgebrochen." };
        }
        const detail = e.response?.data?.detail;
        return { ok: false, error: detail ? formatApiErrorDetail(detail) : (e.message || "Passkey konnte nicht eingerichtet werden.") };
    }
}

export async function listPasskeys() {
    const { data } = await api.get("/auth/webauthn/credentials");
    return data.credentials || [];
}

export async function deletePasskey(id) {
    await api.delete(`/auth/webauthn/credentials/${id}`);
}
